import type { OptionTypeUnite, CommandeOption } from '@/types/commande-globale';

export const formatUniteLabel = (type_unite: OptionTypeUnite): string => {
  const labels: Record<OptionTypeUnite, string> = {
    euro_m3: '€/m³',
    forfait: 'Forfait',
    pourcentage: '%',
    euro_unite: '€/unité',
    euro_heure: '€/heure'
  };
  return labels[type_unite] || type_unite;
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

export const calculateOptionTotalCost = (
  option: CommandeOption,
  volumeTotal: number,
  totalFormules: number
): number => {
  if (!option.active) return 0;
  
  switch (option.unit_type) {
    case 'forfait':
      return option.price;
    case 'euro_m3':
      return option.price * volumeTotal;
    case 'pourcentage':
      return (totalFormules * option.price) / 100;
    case 'euro_unite':
    case 'euro_heure':
      return option.price; // Base, à multiplier par quantité si nécessaire
    default:
      return 0;
  }
};

export const calculateDeliveryOptionCost = (
  price: number,
  unitType: OptionTypeUnite,
  truckVolume: number,
  concretePrice?: number
): number => {
  switch (unitType) {
    case 'euro_m3':
      return price * truckVolume;
    case 'forfait':
      return price;
    case 'pourcentage':
      // Pour les pourcentages, calculer sur le prix du béton si fourni
      if (concretePrice) {
        return (concretePrice * price) / 100;
      }
      return 0;
    case 'euro_unite':
    case 'euro_heure':
      return price;
    default:
      return 0;
  }
};

// Traduction des noms d'options pour l'affichage
export const translateOptionName = (name: string | undefined | null): string => {
  if (!name) return '';
  
  const translations: Record<string, string> = {
    // Transport options
    'Transport concrete mixer truck': 'Transport Malaxeur',
    'Transport concrete mixer truck 8x4': 'Transport Malaxeur 8x4',
    'Transport malaxeur 8x4 (7,5 m3)': 'Transport Malaxeur 8x4',
    'Transport malaxeur 8x4': 'Transport Malaxeur 8x4',
    'Transport malaxeur': 'Transport Malaxeur',
    'concrete_mixer_truck': 'Malaxeur',
    'concrete_mixer_truck_8x4': 'Malaxeur 8x4',
    
    // Environmental/tax contributions
    'Éco-contribution béton': 'Éco-contribution béton',
    'Eco-contribution béton': 'Éco-contribution béton',
    'Contributions diverses': 'Contributions diverses',
    'REP (Responsabilité Élargie)': 'REP',
    'Contribution environnementale': 'Contribution environnementale',
    'Contribution énergétique': 'Contribution énergétique',
    
    // Concrete consistency options
    'Consistance S4': 'Consistance S4',
    'Consistance S5': 'Consistance S5',
    'Consistance S3': 'Consistance S3',
    
    // Pumping options
    'Forfait PUMI 24-28 ml': 'Forfait PUMI 24-28 ml',
    'Forfait PUMI': 'Forfait PUMI',
    'Tapis télescopique': 'Tapis télescopique',
    'Pompage': 'Pompage',
    
    // Access options
    'Grue': 'Accès grue',
    'Tapis': 'Accès tapis',
    'Direct': 'Accès direct',
  };
  
  // Exact match
  if (translations[name]) return translations[name];
  
  // Partial match for variants
  const lowerName = name.toLowerCase();
  if (lowerName.includes('malaxeur') || lowerName.includes('mixer_truck') || lowerName.includes('mixer truck')) {
    if (lowerName.includes('8x4')) return 'Transport Malaxeur 8x4';
    return 'Transport Malaxeur';
  }
  if (lowerName.includes('eco') && lowerName.includes('contribution')) {
    return 'Éco-contribution béton';
  }
  if (lowerName.includes('pumi')) {
    return 'Forfait PUMI';
  }
  
  return name;
};
